import React, { useEffect, useRef, useState, useCallback } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { useLanguage, t } from '../../context/LanguageContext';
import PortalMessagingPanel from './PortalMessagingPanel';

const API_URL = process.env.REACT_APP_API_URL;
const portalAuth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('portalToken')}` } });

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = x => x * Math.PI / 180;
  const dLat = toRad(lat2 - lat1); const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function EventClockButton({ lang, enabled }) {
  const [status, setStatus] = useState(null);
  const [phase, setPhase]   = useState('idle'); // idle | locating | punching | done | err
  const [msg, setMsg]       = useState('');
  const [livePos, setLivePos] = useState(null); // populated when clocked in + venue has coords

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/portal/today-event-punch`, portalAuth());
      setStatus(r.data.canPunch ? r.data : null);
    } catch { setStatus(null); }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load, enabled]);

  // When clocked in with a geolocated venue, silently get position to detect forgotten clock-out
  useEffect(() => {
    if (!status || status.action !== 'clock_out' || status.event?.venueLat == null) {
      setLivePos(null);
      return;
    }
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => setLivePos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setLivePos(null),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [status]);

  const punch = async (lat, lng, forgotten = false) => {
    setPhase('punching');
    try {
      const body = { ...(lat != null ? { lat, lng } : {}), ...(forgotten ? { forgotten: true } : {}) };
      const r = await axios.post(`${API_URL}/api/portal/events/${status.event.id}/punch`, body, portalAuth());
      const done = r.data.action === 'clock_in'
        ? t('Clocked in!', '¡Entrada!', lang)
        : t('Clocked out!', '¡Salida!', lang);
      setMsg(done);
      setPhase('done');
      setLivePos(null);
      await load();
      setTimeout(() => setPhase('idle'), 3000);
    } catch (err) {
      const errMsg = err?.response?.data?.error || t('Failed', 'Error', lang);
      setMsg(errMsg);
      setPhase('err');
      setTimeout(() => setPhase('idle'), 4000);
    }
  };

  const handleClick = async (forgotten = false) => {
    if (!status || phase !== 'idle') return;
    const isIn = status.action === 'clock_out';

    if (isIn) {
      // Clock out — no geofence, works from anywhere
      await punch(null, null, forgotten);
    } else {
      // Clock in — geofence applies
      const ev = status.event;
      if (ev.venueLat != null) {
        setPhase('locating');
        try {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 9000 })
          );
          await punch(pos.coords.latitude, pos.coords.longitude);
        } catch {
          setMsg(t('Location unavailable', 'Ubicación no disponible', lang));
          setPhase('err');
          setTimeout(() => setPhase('idle'), 3000);
        }
      } else {
        await punch(null, null);
      }
    }
  };

  if (!status) return null;

  const isIn   = status.action === 'clock_out';
  const ev     = status.event;
  const busy   = phase === 'locating' || phase === 'punching';
  const isDone = phase === 'done';
  const isErr  = phase === 'err';

  const dist = (livePos && ev.venueLat != null)
    ? Math.round(haversineMeters(livePos.lat, livePos.lng, ev.venueLat, ev.venueLng))
    : null;
  const showForgotBanner = isIn && phase === 'idle' && dist != null && dist > ev.geoRadius;

  const bg    = isDone ? '#198754' : isErr ? '#dc3545' : isIn ? '#dc3545' : '#198754';
  const label = isDone || isErr
    ? msg
    : busy
      ? (phase === 'locating' ? t('Locating…', 'Localizando…', lang) : t('Saving…', 'Guardando…', lang))
      : isIn
        ? t('Clock Out', 'Salida', lang)
        : t('Clock In', 'Entrada', lang);

  return (
    <>
      <button
        onClick={handleClick}
        disabled={busy}
        style={{
          background: bg, color: '#fff', border: 'none', borderRadius: 20,
          padding: '5px 14px', fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
          cursor: busy ? 'default' : 'pointer', maxWidth: 130,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0,
        }}
        title={ev.name}
      >
        {busy && <span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10, borderWidth: 2 }} />}
        {label}
      </button>

      {showForgotBanner && (
        <div style={{
          position: 'fixed', top: 53, left: 0, right: 0, zIndex: 99,
          background: '#fff3cd', borderBottom: '2px solid #ffc107',
          padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#664d03' }}>
              {t('Did you forget to clock out?', '¿Olvidaste registrar tu salida?', lang)}
            </div>
            <div style={{ fontSize: 11, color: '#856404', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ev.name} · {dist} m {t('from venue', 'del lugar', lang)}
            </div>
          </div>
          <button
            className="btn btn-warning btn-sm"
            style={{ fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}
            onClick={() => handleClick(true)}
          >
            {t('Clock Out', 'Salida', lang)}
          </button>
        </div>
      )}
    </>
  );
}

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes

export default function PortalLayout() {
  const { employee, mustChangePassword, logout } = usePortalAuth();
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const [chatOpen, setChatOpen] = useState(false);

  // Event-only staff and drivers (neither are Pizzacini employees) get just their own confined
  // slice; the API refuses everything else for them too (portalAuthMiddleware.js).
  const isEventStaff = employee?.staffType === 'event_staff';
  const isDriver = employee?.staffType === 'driver';

  const ALL_NAV = [
    { to: '/portal',         label: t('Home', 'Inicio', lang),       icon: '🏠', end: true },
    { to: '/portal/hours',   label: t('Hours', 'Horas', lang),       icon: '⏱' },
    { to: '/portal/pay',     label: t('Pay', 'Pago', lang),          icon: '💵' },
    { to: '/portal/pto',     label: t('Time Off', 'Días libres', lang), icon: '🌴' },
    { to: '/portal/points',   label: t('Points', 'Puntos', lang),      icon: '⭐' },
    { to: '/portal/training', label: t('Training', 'Capacitación', lang), icon: '📚' },
    { to: '/portal/deliveries', label: t('Deliveries', 'Entregas', lang), icon: '🖊️' },
    { to: '/portal/events',   label: t('Events', 'Eventos', lang),    icon: '📅' },
    { to: '/portal/profile',  label: t('Profile', 'Perfil', lang),    icon: '👤' },
  ];
  const DRIVER_NAV = [
    { to: '/portal/driver/offers',     label: t('Offers', 'Ofertas', lang), icon: '📦', end: true },
    { to: '/portal/driver/deliveries', label: t('My Deliveries', 'Mis Entregas', lang), icon: '🚚' },
    { to: '/portal/driver/agreement',  label: t('Agreement', 'Acuerdo', lang), icon: '📄' },
    { to: '/portal/profile',           label: t('Profile', 'Perfil', lang), icon: '👤' },
  ];
  const NAV = isDriver ? DRIVER_NAV
    : isEventStaff ? ALL_NAV.filter(n => n.to === '/portal/events' || n.to === '/portal/profile')
    : ALL_NAV;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!employee) { navigate('/portal/login', { replace: true }); return; }
    if (mustChangePassword) { navigate('/portal/change-password', { replace: true }); }
  }, [employee, mustChangePassword, navigate]);

  // Inactivity auto-logout
  useEffect(() => {
    const reset = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => { logout(); navigate('/portal/login', { replace: true }); }, INACTIVITY_MS);
    };
    const events = ['click', 'keydown', 'mousemove', 'touchstart', 'scroll'];
    events.forEach(ev => window.addEventListener(ev, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timerRef.current);
      events.forEach(ev => window.removeEventListener(ev, reset));
    };
  }, [logout, navigate]);

  if (!employee) return null;

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6fa', paddingBottom: 72 }}>
      {/* Top bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e9ecef', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, position: 'sticky', top: 0, zIndex: 100 }}>
        <span style={{ fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
          {t('Hi', 'Hola', lang)}, {employee.firstName} 👋
        </span>
        <EventClockButton lang={lang} enabled={!isDriver} />
        <div className="d-flex align-items-center gap-3" style={{ flexShrink: 0 }}>
          <button
            className="btn btn-link btn-sm p-0 text-muted"
            style={{ fontSize: 12 }}
            onClick={() => setLang(lang === 'en' ? 'es' : 'en')}
          >
            {lang === 'en' ? 'ES' : 'EN'}
          </button>
          {!isEventStaff && (
            <button
              className="btn btn-link btn-sm p-0"
              style={{ fontSize: 18, lineHeight: 1 }}
              onClick={() => setChatOpen(true)}
              title={t('Team Messages', 'Mensajes', lang)}
            >
              💬
            </button>
          )}
          <button className="btn btn-link btn-sm text-muted p-0" style={{ fontSize: 13 }} onClick={() => { logout(); navigate('/portal/login'); }}>
            {t('Sign out', 'Cerrar sesión', lang)}
          </button>
        </div>
      </div>

      {/* Page content */}
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 12px' }}>
        <Outlet />
      </div>

      {/* Bottom navigation */}
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#fff', borderTop: '1px solid #e9ecef',
        display: 'flex', zIndex: 200,
      }}>
        {NAV.map(({ to, label, icon, end }) => (
          <NavLink
            key={to} to={to} end={end}
            style={({ isActive }) => ({
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '8px 4px 6px', textDecoration: 'none', fontSize: 10,
              color: isActive ? '#0d6efd' : '#6c757d',
              borderTop: isActive ? '2px solid #0d6efd' : '2px solid transparent',
              fontWeight: isActive ? 700 : 400,
            })}
          >
            <span style={{ fontSize: 20, lineHeight: 1.2 }}>{icon}</span>
            {label}
          </NavLink>
        ))}
      </nav>

      {!isEventStaff && <PortalMessagingPanel open={chatOpen} onClose={() => setChatOpen(false)} lang={lang} />}
    </div>
  );
}
